
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MethodEntity, KnowledgeEntity, Question, IdiomData } from '../types';
import { getFullCategoryTree } from '../services/categoryService';
import { getMethods, saveMethod, deleteMethod, getQuestions, getKnowledge, saveKnowledge, deleteKnowledge, hydrateMethod, hydrateQuestion } from '../services/storageService';
import { explainIdiom } from '../services/geminiService';
import RichTextEditor from '../components/RichTextEditor';
import { 
    Lightbulb, ChevronRight, Plus, Tag, BookOpen, 
    X, Check, Trash2, Edit2, Link as LinkIcon, Eye,
    ArrowLeft, Search, Layers, Folder, Globe, Feather, Zap, 
    Maximize2, Minimize2, PanelLeftClose, PanelLeftOpen, ZoomIn, Clock, GripVertical, Sparkles, Book, Hash, FileText, ChevronLeft
} from 'lucide-react';

// 预设标签库
const KNOWLEDGE_TAG_PRESETS = {
    'COMMON_SENSE': ['法律常识', '人文历史', '科技生活', '地理国情', '经济常识', '管理公文', '二十四节气'],
    'CURRENT_AFFAIRS': ['重要会议', '重要文件', '科技成就', '国际时政', '人物事迹', '周年纪念', '体育赛事'],
    'IDIOM': []
};

const ITEMS_PER_PAGE = 12;

const Methodology = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Layout State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);

  // Main Tabs
  const [activeTab, setActiveTab] = useState<'mindmap' | 'knowledge'>('mindmap');
  
  // Knowledge Sub-Tabs & Filters
  const [knowledgeTab, setKnowledgeTab] = useState<'IDIOM' | 'COMMON_SENSE' | 'CURRENT_AFFAIRS'>('IDIOM');
  const [knowledgeTagFilter, setKnowledgeTagFilter] = useState<string>('ALL');
  const [knowledgeSearchTerm, setKnowledgeSearchTerm] = useState('');

  const [methods, setMethods] = useState<MethodEntity[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeEntity[]>([]);
  
  // Tree State
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<{name: string, type: 'category' | 'subCategory' | 'tag'} | null>(null);
  
  // Pagination State
  const [methodPage, setMethodPage] = useState(1);
  const [knowledgePage, setKnowledgePage] = useState(1);

  // Detail View State (New)
  const [viewingDetailId, setViewingDetailId] = useState<string | null>(null);
  const [viewingType, setViewingType] = useState<'method' | 'knowledge'>('method');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Method Editor State
  const [isEditingMethod, setIsEditingMethod] = useState(false);
  const [methodForm, setMethodForm] = useState<Partial<MethodEntity>>({});
  
  // Knowledge Editor State
  const [isEditingKnowledge, setIsEditingKnowledge] = useState(false);
  const [knowledgeForm, setKnowledgeForm] = useState<Partial<KnowledgeEntity>>({ type: 'IDIOM', tags: [] });
  const [knowledgeTagInput, setKnowledgeTagInput] = useState('');
  const [idiomForm, setIdiomForm] = useState<IdiomData>({ word: '', meaning: '', usage: '', example: '' });
  const [isIdiomLoading, setIsIdiomLoading] = useState(false);

  // Question Detail Overlay
  const [viewingQuestion, setViewingQuestion] = useState<Question | null>(null);

  const fullTree = useMemo(() => getFullCategoryTree(), []);

  // Compute currently viewing item reactively
  const viewingItem = useMemo(() => {
      if (!viewingDetailId) return null;
      if (viewingType === 'method') return methods.find(m => m.id === viewingDetailId);
      return knowledgeList.find(k => k.id === viewingDetailId);
  }, [viewingDetailId, viewingType, methods, knowledgeList]);

  useEffect(() => {
    const savedWidth = localStorage.getItem('xingce_sidebar_width');
    if (savedWidth) setSidebarWidth(parseInt(savedWidth));

    const init = async () => {
        const [m, q, k] = await Promise.all([getMethods(), getQuestions(), getKnowledge()]);
        setMethods(m);
        setQuestions(q);
        setKnowledgeList(k);
    };
    init();
  }, [location.state]);

  // Reset Pages when filters change
  useEffect(() => {
      setKnowledgeTagFilter('ALL');
      setKnowledgeSearchTerm('');
      setKnowledgePage(1);
  }, [knowledgeTab]);

  useEffect(() => {
      setMethodPage(1);
  }, [selectedNode]);

  // --- Resizer Logic ---
  const startResizing = (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const newWidth = Math.max(64, Math.min(600, e.clientX - 24)); 
      setSidebarWidth(newWidth);
      if (newWidth < 100 && isSidebarOpen) setIsSidebarOpen(false);
      if (newWidth >= 100 && !isSidebarOpen) setIsSidebarOpen(true);
  };

  const handleMouseUp = () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('xingce_sidebar_width', sidebarWidth.toString());
  };

  // --- Tree Logic ---
  const toggleExpand = (key: string) => {
      setExpandedKeys(prev => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
      });
  };

  const renderTree = (nodes: any[], depth: number = 0, type: 'category' | 'subCategory' | 'tag' = 'category') => {
      return nodes.map(node => {
          const key = node.name;
          const isLeaf = !node.children || node.children.length === 0;
          const isExpanded = expandedKeys.has(key);
          const isSelected = selectedNode?.name === node.name;
          const nextType = type === 'category' ? 'subCategory' : 'tag';

          return (
              <div key={key} className="select-none">
                  <div 
                      className={`flex items-center px-3 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap overflow-hidden ${
                          isSelected ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      style={{ 
                          marginLeft: isSidebarOpen ? `${depth * 12}px` : '0', 
                          justifyContent: isSidebarOpen ? 'flex-start' : 'center',
                          padding: isSidebarOpen ? '6px 12px' : '8px'
                      }}
                      onClick={() => {
                          setSelectedNode({ name: node.name, type });
                          if (!isLeaf) toggleExpand(key);
                      }}
                      title={node.name}
                  >
                      {isSidebarOpen ? (
                          <>
                              {!isLeaf ? (
                                  <ChevronRight size={14} className={`mr-1.5 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                              ) : <div className="w-4"></div>}
                              <span className={`text-sm truncate ${type === 'category' ? 'font-bold' : type === 'subCategory' ? 'font-medium' : ''}`}>{node.name}</span>
                          </>
                      ) : (
                          <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-white' : type === 'category' ? 'bg-purple-400' : 'bg-blue-400'}`}></div>
                      )}
                  </div>
                  {isExpanded && node.children && isSidebarOpen && <div className="mt-0.5">{renderTree(node.children, depth + 1, nextType)}</div>}
              </div>
          );
      });
  };

  // --- Method Logic (Updated for Sorting & Pagination) ---
  const displayedMethods = useMemo(() => {
      // Sort by Update Time Descending
      const sorted = [...methods].sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt));
      
      if (!selectedNode) return sorted; // Return ALL if no node selected
      
      const { name, type } = selectedNode;
      return sorted.filter(m => {
          if (type === 'tag' && m.tag === name) return true;
          if (type === 'subCategory' && m.subCategory === name && !m.tag) return true;
          if (type === 'category' && m.category === name && !m.subCategory && !m.tag) return true;
          return false;
      });
  }, [methods, selectedNode]);

  const paginatedMethods = useMemo(() => {
      const start = (methodPage - 1) * ITEMS_PER_PAGE;
      return displayedMethods.slice(start, start + ITEMS_PER_PAGE);
  }, [displayedMethods, methodPage]);

  const methodTotalPages = Math.ceil(displayedMethods.length / ITEMS_PER_PAGE);

  const handleCreateMethod = () => {
      const base: Partial<MethodEntity> = {
          id: Date.now().toString(), title: '', content: '', createdAt: Date.now()
      };
      if (selectedNode) {
          if (selectedNode.type === 'category') base.category = selectedNode.name;
          else if (selectedNode.type === 'subCategory') base.subCategory = selectedNode.name;
          else if (selectedNode.type === 'tag') base.tag = selectedNode.name;
      }
      setMethodForm(base); setIsEditingMethod(true);
  };

  const handleEditMethod = async (m: MethodEntity) => {
      const full = await hydrateMethod({ ...m });
      setMethodForm(full);
      setIsEditingMethod(true);
  };

  const handleSaveMethod = async () => {
      if (!methodForm.title?.trim()) { alert("标题不能为空"); return; }
      const m = { ...methodForm, updatedAt: Date.now() } as MethodEntity;
      await saveMethod(m);
      setMethods(prev => {
          const idx = prev.findIndex(x => x.id === m.id);
          if (idx >= 0) { const arr = [...prev]; arr[idx] = m; return arr; }
          return [m, ...prev];
      });
      setIsEditingMethod(false);
  };
  
  const handleDeleteMethod = async (id: string) => {
      if (!window.confirm("确定删除此方法论？")) return;
      await deleteMethod(id);
      setMethods(prev => prev.filter(m => m.id !== id));
      if (viewingDetailId === id) setViewingDetailId(null);
  };

  // --- Knowledge Logic (Updated for Sorting & Pagination) ---
  const handleEditKnowledge = (k?: KnowledgeEntity) => {
      setKnowledgeTagInput('');
      if (k) {
          setKnowledgeForm({ ...k, tags: k.tags || [] });
          if (k.type === 'IDIOM' && k.idiomData) {
              setIdiomForm(k.idiomData);
          } else {
              setIdiomForm({ word: k.title || '', meaning: '', usage: '', example: '' });
          }
      } else {
          setKnowledgeForm({ 
              id: Date.now().toString(), 
              title: '', 
              content: '', 
              type: knowledgeTab, 
              tags: [],
              createdAt: Date.now() 
          });
          setIdiomForm({ word: '', meaning: '', usage: '', example: '' });
      }
      setIsEditingKnowledge(true);
  };

  const handleAutoFillIdiom = async () => {
      const word = idiomForm.word || knowledgeForm.title;
      if (!word?.trim()) { alert("请先输入成语"); return; }
      
      setIsIdiomLoading(true);
      try {
          const result = await explainIdiom(word);
          setIdiomForm(prev => ({
              ...prev,
              word: word,
              meaning: result.meaning || '',
              usage: result.usage || '',
              example: result.example || ''
          }));
      } catch (e) {
          alert("AI 填充失败，请重试");
      } finally {
          setIsIdiomLoading(false);
      }
  };

  const handleSaveKnowledge = async () => {
      let k = { ...knowledgeForm, createdAt: knowledgeForm.createdAt || Date.now() } as KnowledgeEntity;
      
      if (k.type === 'IDIOM') {
          if (!idiomForm.word.trim()) { alert("成语不能为空"); return; }
          k.title = idiomForm.word;
          k.idiomData = idiomForm;
          // Construct HTML content for generic display compatibility
          k.content = `
            <div class="idiom-card">
                <p><strong>本意：</strong>${idiomForm.meaning}</p>
                <p><strong>用法：</strong>${idiomForm.usage}</p>
                <p><strong>例句：</strong>${idiomForm.example}</p>
            </div>
          `;
      } else {
          if (!k.title?.trim() && !k.content?.trim()) { alert("内容不能为空"); return; }
          if (!k.title) k.title = '无标题知识点';
      }
      
      await saveKnowledge(k);
      setKnowledgeList(prev => {
          const idx = prev.findIndex(x => x.id === k.id);
          if (idx >= 0) { const arr = [...prev]; arr[idx] = k; return arr; }
          return [k, ...prev];
      });
      setIsEditingKnowledge(false);
  };

  const handleDeleteKnowledge = async (id: string) => {
      if (!window.confirm("确定删除此知识点？")) return;
      await deleteKnowledge(id);
      setKnowledgeList(prev => prev.filter(k => k.id !== id));
      if (viewingDetailId === id) setViewingDetailId(null);
  };

  const handleAddKnowledgeTag = (tag: string) => {
      const t = tag.trim();
      if (t && !knowledgeForm.tags?.includes(t)) {
          setKnowledgeForm(prev => ({ ...prev, tags: [...(prev.tags || []), t] }));
      }
      setKnowledgeTagInput('');
  };

  const handleRemoveKnowledgeTag = (tag: string) => {
      setKnowledgeForm(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }));
  };

  const getLinkedQuestions = (id: string, type: 'method' | 'knowledge') => {
      if (type === 'method') return questions.filter(q => q.methodIds?.includes(id));
      return questions.filter(q => q.knowledgeIds?.includes(id));
  };

  const openQuestion = async (q: Question) => {
      let full = q;
      if (q.materials.some(m => m === '__IMAGE_REF__') || q.notesImage === '__IMAGE_REF__') full = await hydrateQuestion(q);
      setViewingQuestion(full);
  };

  const openDetail = (id: string, type: 'method' | 'knowledge') => {
      if (type === 'method') {
          const m = methods.find(x => x.id === id);
          if (m && m.content.includes('__METHOD_REF__')) {
              hydrateMethod(m).then(() => {
                  setMethods(prev => [...prev]);
              });
          }
      }
      setViewingDetailId(id);
      setViewingType(type);
  };

  // Filter knowledge list based on active tab and tag filter
  const filteredKnowledge = useMemo(() => {
      // Sort by Created Time Descending
      const sorted = [...knowledgeList].sort((a, b) => b.createdAt - a.createdAt);

      return sorted.filter(k => {
          if (k.type !== knowledgeTab) return false;
          if (knowledgeTagFilter !== 'ALL' && !k.tags?.includes(knowledgeTagFilter)) return false;
          
          if (knowledgeSearchTerm.trim()) {
              const term = knowledgeSearchTerm.toLowerCase();
              const matchesTitle = (k.title || '').toLowerCase().includes(term);
              const matchesTags = k.tags?.some(t => t.toLowerCase().includes(term));
              const matchesContent = k.idiomData 
                  ? (k.idiomData.meaning.includes(term) || k.idiomData.usage.includes(term))
                  : k.content.toLowerCase().includes(term);
              return matchesTitle || matchesTags || matchesContent;
          }
          return true;
      });
  }, [knowledgeList, knowledgeTab, knowledgeTagFilter, knowledgeSearchTerm]);

  const paginatedKnowledge = useMemo(() => {
      const start = (knowledgePage - 1) * ITEMS_PER_PAGE;
      return filteredKnowledge.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredKnowledge, knowledgePage]);

  const knowledgeTotalPages = Math.ceil(filteredKnowledge.length / ITEMS_PER_PAGE);

  // Collect all unique tags for current tab for the filter bar
  const availableFilterTags = useMemo(() => {
      const tags = new Set<string>();
      knowledgeList.filter(k => k.type === knowledgeTab).forEach(k => k.tags?.forEach(t => tags.add(t)));
      return Array.from(tags).sort();
  }, [knowledgeList, knowledgeTab]);

  // Handle Image Click in Detail View for Lightbox
  const handleContentClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
          setLightboxImage((target as HTMLImageElement).src);
      }
  };

  // Helper to render bold text from **text** format
  const renderWithBold = (text: string) => {
      if (!text) return null;
      // Regex to match **text**, allowing newlines
      return text.split(/(\*\*[\s\S]+?\*\*)/g).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} className="font-bold text-slate-900 bg-yellow-100/50 px-0.5 rounded border-b border-yellow-200/50">{part.slice(2, -2)}</strong>;
          }
          return part;
      });
  };

  // Pagination Controls Component
  const Pagination = ({ currentPage, totalPages, onPageChange }: { currentPage: number, totalPages: number, onPageChange: (p: number) => void }) => {
      if (totalPages <= 1) return null;
      return (
          <div className="flex justify-center items-center gap-4 py-6">
              <button 
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))} 
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white border border-gray-200 text-slate-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              >
                  <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-medium text-slate-600">
                  {currentPage} / {totalPages}
              </span>
              <button 
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} 
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-white border border-gray-200 text-slate-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              >
                  <ChevronRight size={18} />
              </button>
          </div>
      );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col h-screen overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center flex-shrink-0 z-10 shadow-sm">
          <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg"><Layers size={20} /></div>
              <div><h1 className="font-bold text-lg text-slate-800">方法沉淀</h1></div>
          </div>
          <div className="flex bg-gray-100 p-1 rounded-lg">
              <button onClick={() => setActiveTab('mindmap')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${activeTab === 'mindmap' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>方法论</button>
              <button onClick={() => setActiveTab('knowledge')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${activeTab === 'knowledge' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500'}`}>知识碎片</button>
          </div>
      </div>

      <div className="flex-1 flex overflow-hidden p-4 sm:p-6 max-w-[1920px] mx-auto w-full gap-4">
        {activeTab === 'mindmap' ? (
            <>
                {/* Resizable Sidebar */}
                <div 
                    ref={sidebarRef}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden flex-shrink-0 relative group"
                    style={{ width: isSidebarOpen ? sidebarWidth : 64, transition: isResizingRef.current ? 'none' : 'width 0.3s ease' }}
                >
                    <div className="p-3 border-b border-gray-50 bg-gray-50 text-xs font-bold text-slate-400 uppercase flex justify-between items-center whitespace-nowrap overflow-hidden">
                        {isSidebarOpen && <span>体系导航</span>}
                        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-1 hover:bg-gray-200 rounded text-slate-500">
                            {isSidebarOpen ? <PanelLeftClose size={16}/> : <PanelLeftOpen size={16}/>}
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                        {renderTree(fullTree)}
                    </div>
                    
                    {/* Drag Handle */}
                    {isSidebarOpen && (
                        <div 
                            className="absolute top-0 right-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-400/50 transition-colors z-10 flex items-center justify-center group/handle"
                            onMouseDown={startResizing}
                        >
                            <div className="h-8 w-1 bg-slate-300 rounded-full opacity-0 group-hover/handle:opacity-100"></div>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden relative">
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="font-bold text-slate-700 flex items-center text-lg">
                                    {selectedNode ? (
                                        <>
                                            {selectedNode.type === 'category' ? <Layers size={18} className="mr-2 text-purple-500"/> : selectedNode.type === 'subCategory' ? <Folder size={18} className="mr-2 text-blue-500"/> : <Tag size={18} className="mr-2 text-green-500"/>}
                                            {selectedNode.name}
                                        </>
                                    ) : (
                                        <>
                                            <Globe size={18} className="mr-2 text-blue-600"/>
                                            全部方法论
                                        </>
                                    )}
                                </h2>
                                <p className="text-xs text-slate-400 mt-1">
                                    {selectedNode ? '在此节点挂载通用方法论' : '按更新时间展示所有方法'}
                                </p>
                            </div>
                            <button onClick={handleCreateMethod} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center shadow hover:bg-blue-700">
                                <Plus size={16} className="mr-1.5"/> 新增方法
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30 custom-scrollbar space-y-8">
                            <div>
                                <h3 className="text-xs font-bold text-slate-500 uppercase mb-3 text-blue-600 flex justify-between items-center">
                                    <span>方法论列表 ({displayedMethods.length})</span>
                                    <span className="text-xs text-slate-400 font-normal">按更新时间降序</span>
                                </h3>
                                {paginatedMethods.length === 0 ? (
                                    <div className="py-8 text-center text-slate-400 border border-dashed border-gray-200 rounded-xl">暂无方法论</div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {paginatedMethods.map(m => (
                                                <div 
                                                    key={m.id} 
                                                    onClick={() => openDetail(m.id, 'method')}
                                                    className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col h-64"
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <h3 className="font-bold text-base text-slate-800 line-clamp-1">{m.title}</h3>
                                                        <div className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn size={16}/></div>
                                                    </div>
                                                    {/* Node Info Badges */}
                                                    <div className="flex flex-wrap gap-1 mb-3">
                                                        {m.category && <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded">{m.category}</span>}
                                                        {m.subCategory && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{m.subCategory}</span>}
                                                        {m.tag && <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded flex items-center"><Tag size={8} className="mr-0.5"/>{m.tag}</span>}
                                                    </div>
                                                    <div className="text-sm text-slate-500 line-clamp-4 mb-auto flex-1 opacity-80" dangerouslySetInnerHTML={{__html: m.content.replace(/<[^>]+>/g, '') || '点击查看详情...'}}></div>
                                                    <div className="text-xs text-slate-400 pt-3 border-t border-gray-50 flex justify-between items-center mt-2">
                                                        <span>{new Date(m.updatedAt || m.createdAt).toLocaleDateString()}</span>
                                                        <span className="flex items-center text-blue-500 bg-blue-50 px-2 py-0.5 rounded">
                                                            <LinkIcon size={10} className="mr-1"/> {getLinkedQuestions(m.id, 'method').length} 题
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <Pagination currentPage={methodPage} totalPages={methodTotalPages} onPageChange={setMethodPage} />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        ) : (
            <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Knowledge Tabs Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-3 bg-gray-50/50">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2">
                            <button 
                                onClick={() => setKnowledgeTab('IDIOM')} 
                                className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center transition-all ${knowledgeTab === 'IDIOM' ? 'bg-purple-100 text-purple-700 shadow-sm ring-1 ring-purple-200' : 'text-slate-500 hover:bg-gray-100'}`}
                            >
                                <Feather size={16} className="mr-2"/> 成语积累
                            </button>
                            <button 
                                onClick={() => setKnowledgeTab('COMMON_SENSE')} 
                                className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center transition-all ${knowledgeTab === 'COMMON_SENSE' ? 'bg-emerald-100 text-emerald-700 shadow-sm ring-1 ring-emerald-200' : 'text-slate-500 hover:bg-gray-100'}`}
                            >
                                <Globe size={16} className="mr-2"/> 常识积累
                            </button>
                            <button 
                                onClick={() => setKnowledgeTab('CURRENT_AFFAIRS')} 
                                className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center transition-all ${knowledgeTab === 'CURRENT_AFFAIRS' ? 'bg-rose-100 text-rose-700 shadow-sm ring-1 ring-rose-200' : 'text-slate-500 hover:bg-gray-100'}`}
                            >
                                <Zap size={16} className="mr-2"/> 时政积累
                            </button>
                        </div>
                        <button onClick={() => handleEditKnowledge()} className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow hover:bg-amber-600 transition-all flex items-center">
                            <Plus size={18} className="mr-1.5"/> 新增{knowledgeTab === 'IDIOM' ? '成语' : knowledgeTab === 'COMMON_SENSE' ? '常识' : '时政'}
                        </button>
                    </div>
                    
                    {/* Search & Tag Filter Bar */}
                    <div className="flex flex-col sm:flex-row gap-3 items-center">
                        {/* Search Input */}
                        <div className="relative flex-1 w-full">
                             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                             <input 
                                type="text" 
                                placeholder="搜索知识点 (标题/标签/内容)..." 
                                className="w-full pl-9 pr-4 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                                value={knowledgeSearchTerm}
                                onChange={e => setKnowledgeSearchTerm(e.target.value)}
                             />
                        </div>

                        {/* Tag Filter */}
                        {availableFilterTags.length > 0 && (
                            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full sm:w-auto">
                                <span className="text-xs font-bold text-slate-400 uppercase flex-shrink-0 mr-1"><FilterIcon size={12} className="inline mr-1"/>标签:</span>
                                <button 
                                    onClick={() => setKnowledgeTagFilter('ALL')}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border flex-shrink-0 ${knowledgeTagFilter === 'ALL' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-gray-200 hover:border-slate-300'}`}
                                >
                                    全部
                                </button>
                                {availableFilterTags.map(tag => (
                                    <button 
                                        key={tag}
                                        onClick={() => setKnowledgeTagFilter(tag)}
                                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border whitespace-nowrap flex-shrink-0 ${knowledgeTagFilter === tag ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'}`}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Knowledge Grid */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30 custom-scrollbar">
                    {paginatedKnowledge.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-300">
                            <BookOpen size={48} className="mb-4 opacity-20"/>
                            <p>{knowledgeSearchTerm ? '未找到匹配的知识点' : '暂无相关积累，点击右上角添加'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                {paginatedKnowledge.map(k => {
                                    const linkedQs = getLinkedQuestions(k.id, 'knowledge');
                                    const bgClass = k.type === 'IDIOM' ? 'hover:border-purple-300 hover:bg-purple-50/30' : k.type === 'COMMON_SENSE' ? 'hover:border-emerald-300 hover:bg-emerald-50/30' : 'hover:border-rose-300 hover:bg-rose-50/30';
                                    const borderClass = k.type === 'IDIOM' ? 'border-l-4 border-l-purple-400' : k.type === 'COMMON_SENSE' ? 'border-l-4 border-l-emerald-400' : 'border-l-4 border-l-rose-400';
                                    
                                    return (
                                        <div 
                                            key={k.id} 
                                            onClick={() => openDetail(k.id, 'knowledge')}
                                            className={`group bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all relative flex flex-col cursor-pointer ${bgClass} ${borderClass}`}
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="font-bold text-slate-700 text-lg line-clamp-2 flex-1 pr-1" title={k.title}>{k.title || '无标题'}</h4>
                                                <div className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn size={14}/></div>
                                            </div>
                                            
                                            {/* Tags Display on Card */}
                                            <div className="flex flex-wrap gap-1 mb-3 flex-1 content-start">
                                                {k.tags && k.tags.length > 0 ? k.tags.slice(0, 3).map(t => (
                                                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded border border-gray-200">{t}</span>
                                                )) : <span className="text-[10px] text-slate-300 italic">无标签</span>}
                                                {k.tags && k.tags.length > 3 && <span className="text-[10px] text-slate-400">+{k.tags.length - 3}</span>}
                                            </div>

                                            <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-[10px] text-slate-400">
                                                <span>{new Date(k.createdAt).toLocaleDateString()}</span>
                                                {linkedQs.length > 0 && (
                                                    <span 
                                                        className="flex items-center text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-blue-100"
                                                        onClick={(e) => { e.stopPropagation(); openQuestion(linkedQs[0]); }}
                                                    >
                                                        <LinkIcon size={10} className="mr-1"/> {linkedQs.length}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <Pagination currentPage={knowledgePage} totalPages={knowledgeTotalPages} onPageChange={setKnowledgePage} />
                        </>
                    )}
                </div>
            </div>
        )}

        {/* Detail View Modal (New) */}
        {viewingDetailId && viewingItem && (
            <div className="absolute inset-0 z-40 bg-black/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-200" onClick={(e) => { if(e.target === e.currentTarget) setViewingDetailId(null); }}>
                <div className="w-full max-w-4xl bg-white h-full shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col relative">
                    <div className="p-5 border-b border-gray-100 flex justify-between items-start bg-gray-50/80 backdrop-blur-sm sticky top-0 z-10">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-2xl font-bold text-slate-800 leading-tight">{viewingItem.title || '未命名内容'}</h2>
                                {(viewingItem as KnowledgeEntity).tags?.map(t => (
                                    <span key={t} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100 font-medium">{t}</span>
                                ))}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                                {viewingType === 'method' && (viewingItem as MethodEntity).tag && (
                                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold flex items-center">
                                        <Tag size={12} className="mr-1"/> {(viewingItem as MethodEntity).tag}
                                    </span>
                                )}
                                {viewingType === 'knowledge' && (
                                    <span className="bg-gray-100 text-slate-600 px-2 py-0.5 rounded font-bold">
                                        {(viewingItem as KnowledgeEntity).type === 'IDIOM' ? '成语积累' : (viewingItem as KnowledgeEntity).type === 'COMMON_SENSE' ? '常识积累' : '时政积累'}
                                    </span>
                                )}
                                <span className="flex items-center"><Clock size={12} className="mr-1"/> {new Date(viewingItem.createdAt).toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => { 
                                    if(viewingType === 'method') handleEditMethod(viewingItem as MethodEntity);
                                    else handleEditKnowledge(viewingItem as KnowledgeEntity);
                                }} 
                                className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="编辑"
                            >
                                <Edit2 size={20}/>
                            </button>
                            <button 
                                onClick={() => { 
                                    if(viewingType === 'method') handleDeleteMethod(viewingItem.id);
                                    else handleDeleteKnowledge(viewingItem.id);
                                }} 
                                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="删除"
                            >
                                <Trash2 size={20}/>
                            </button>
                            <div className="w-px h-6 bg-gray-300 mx-1"></div>
                            <button onClick={() => setViewingDetailId(null)} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-gray-100 rounded-full transition-colors">
                                <X size={24}/>
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                        {(viewingItem as KnowledgeEntity).idiomData ? (
                            <div className="space-y-6">
                                <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
                                    <h3 className="font-bold text-purple-900 text-lg mb-2 flex items-center"><Book size={20} className="mr-2"/> 本意</h3>
                                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingItem as KnowledgeEntity).idiomData?.meaning || '')}</p>
                                </div>
                                <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                                    <h3 className="font-bold text-blue-900 text-lg mb-2 flex items-center"><Zap size={20} className="mr-2"/> 用法与语境</h3>
                                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingItem as KnowledgeEntity).idiomData?.usage || '')}</p>
                                </div>
                                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <h3 className="font-bold text-gray-700 text-lg mb-2 flex items-center"><Feather size={20} className="mr-2"/> 例句</h3>
                                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingItem as KnowledgeEntity).idiomData?.example || '')}</p>
                                </div>
                            </div>
                        ) : (
                            <div 
                                className="bg-white text-slate-700 rich-text-display cursor-text"
                                dangerouslySetInnerHTML={{ __html: viewingItem.content }}
                                onClick={handleContentClick}
                            ></div>
                        )}
                        
                        {/* Linked Questions Footer */}
                        <div className="mt-12 pt-8 border-t border-gray-100">
                            <h4 className="font-bold text-slate-600 mb-4 flex items-center">
                                <LinkIcon size={18} className="mr-2"/> 关联错题回顾
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {getLinkedQuestions(viewingItem.id, viewingType).map(q => (
                                    <div 
                                        key={q.id} 
                                        onClick={() => openQuestion(q)}
                                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all flex items-start"
                                    >
                                        <span className="bg-white border border-gray-200 text-slate-500 text-xs px-1.5 py-0.5 rounded mr-2 flex-shrink-0 mt-0.5">{q.subCategory || q.category}</span>
                                        <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed">{q.stem}</p>
                                    </div>
                                ))}
                                {getLinkedQuestions(viewingItem.id, viewingType).length === 0 && (
                                    <div className="text-slate-400 text-sm italic col-span-2">暂无关联题目</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Lightbox for Image Zoom */}
        {lightboxImage && (
            <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center animate-in fade-in duration-200" onClick={() => setLightboxImage(null)}>
                <img 
                    src={lightboxImage} 
                    alt="Zoomed" 
                    className="max-w-full max-h-full object-contain p-4 transition-transform duration-200" 
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                />
                <button className="absolute top-6 right-6 text-white/50 hover:text-white p-2" onClick={() => setLightboxImage(null)}>
                    <X size={40} />
                </button>
            </div>
        )}

        {/* Method Editor Modal */}
        {isEditingMethod && (
            <div className="absolute inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800 text-lg">编辑方法论</h3>
                        <button onClick={() => setIsEditingMethod(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} className="text-slate-400"/></button>
                    </div>
                    <div className="p-6 flex-1 overflow-hidden flex flex-col space-y-4">
                        <div>
                            <input type="text" placeholder="方法标题" className="w-full border-b border-gray-200 p-2 text-xl font-bold focus:border-blue-500 outline-none" value={methodForm.title} onChange={e => setMethodForm({...methodForm, title: e.target.value})} autoFocus />
                        </div>
                        <div className="flex-1 flex flex-col overflow-hidden">
                            <RichTextEditor value={methodForm.content || ''} onChange={h => setMethodForm({...methodForm, content: h})} placeholder="输入详细的方法论内容..." />
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                        <button onClick={() => setIsEditingMethod(false)} className="px-5 py-2.5 text-sm text-slate-600 font-bold hover:bg-gray-200 rounded-xl transition-colors">取消</button>
                        <button onClick={handleSaveMethod} className="bg-blue-600 text-white px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg hover:bg-blue-700 transition-all hover:scale-105">保存方法</button>
                    </div>
                </div>
            </div>
        )}

        {/* Knowledge Editor Modal (Adaptive) */}
        {isEditingKnowledge && (
            <div className="absolute inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <h3 className="font-bold text-slate-800 text-lg">编辑知识点</h3>
                            <select 
                                value={knowledgeForm.type} 
                                onChange={e => {
                                    setKnowledgeForm({...knowledgeForm, type: e.target.value as any});
                                    if(e.target.value !== 'IDIOM') setIdiomForm({ word: '', meaning: '', usage: '', example: '' });
                                }}
                                className="bg-gray-100 border-transparent rounded-lg text-sm font-bold px-3 py-1 text-slate-600 focus:bg-white focus:ring-2 focus:ring-blue-200 outline-none"
                            >
                                <option value="IDIOM">成语积累</option>
                                <option value="COMMON_SENSE">常识积累</option>
                                <option value="CURRENT_AFFAIRS">时政积累</option>
                            </select>
                        </div>
                        <button onClick={() => setIsEditingKnowledge(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} className="text-slate-400"/></button>
                    </div>
                    
                    <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                        {knowledgeForm.type === 'IDIOM' ? (
                            <div className="flex flex-col lg:flex-row gap-6 h-full">
                                {/* Left: Inputs */}
                                <div className="flex-1 space-y-6 overflow-y-auto">
                                    <div className="flex gap-4 items-end">
                                        <div className="flex-1">
                                            <label className="block text-sm font-bold text-slate-700 mb-1">成语词语</label>
                                            <input 
                                                type="text" 
                                                placeholder="输入成语..." 
                                                className="w-full border-2 border-purple-100 rounded-xl p-3 text-lg font-bold focus:border-purple-500 outline-none text-purple-900 bg-purple-50/50"
                                                value={idiomForm.word}
                                                onChange={e => {
                                                    setIdiomForm({...idiomForm, word: e.target.value});
                                                    setKnowledgeForm({...knowledgeForm, title: e.target.value});
                                                }}
                                            />
                                        </div>
                                        <button 
                                            onClick={handleAutoFillIdiom} 
                                            disabled={isIdiomLoading || !idiomForm.word}
                                            className="h-[52px] px-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:shadow-none flex items-center whitespace-nowrap"
                                        >
                                            {isIdiomLoading ? <Sparkles className="animate-spin mr-1" /> : <Sparkles className="mr-1" />}
                                            {isIdiomLoading ? '生成中' : 'AI 填充'}
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-bold text-slate-500 mb-1 flex justify-between">
                                                <span>本意 / 释义</span>
                                                <span className="text-xs font-normal text-slate-400">支持 **加粗**</span>
                                            </label>
                                            <textarea 
                                                className="w-full border border-gray-200 rounded-xl p-3 h-24 focus:ring-2 focus:ring-purple-200 outline-none text-sm leading-relaxed"
                                                placeholder="成语的原始含义..."
                                                value={idiomForm.meaning}
                                                onChange={e => setIdiomForm({...idiomForm, meaning: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-slate-500 mb-1 flex justify-between">
                                                <span>用法 / 语境</span>
                                                <span className="text-xs font-normal text-slate-400">支持 **加粗**</span>
                                            </label>
                                            <textarea 
                                                className="w-full border border-gray-200 rounded-xl p-3 h-24 focus:ring-2 focus:ring-blue-200 outline-none text-sm leading-relaxed"
                                                placeholder="在逻辑填空中常见的搭配对象、褒贬色彩..."
                                                value={idiomForm.usage}
                                                onChange={e => setIdiomForm({...idiomForm, usage: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-slate-500 mb-1 flex justify-between">
                                                <span>例句</span>
                                                <span className="text-xs font-normal text-slate-400">支持 **加粗**</span>
                                            </label>
                                            <textarea 
                                                className="w-full border border-gray-200 rounded-xl p-3 h-24 focus:ring-2 focus:ring-green-200 outline-none bg-gray-50 text-sm leading-relaxed"
                                                placeholder="标准用法例句..."
                                                value={idiomForm.example}
                                                onChange={e => setIdiomForm({...idiomForm, example: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Real-time Preview */}
                                <div className="w-full lg:w-[320px] bg-gray-50 rounded-2xl p-4 border border-gray-100 flex flex-col flex-shrink-0">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center"><Eye size={12} className="mr-1"/> 效果预览</h4>
                                    <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1">
                                        <div className="bg-white p-3 rounded-lg border border-purple-100 shadow-sm">
                                            <div className="flex items-center gap-2 mb-1 border-b border-purple-50 pb-1">
                                                <Book size={14} className="text-purple-500"/>
                                                <span className="text-xs font-bold text-purple-900">本意</span>
                                            </div>
                                            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                                                {idiomForm.meaning ? renderWithBold(idiomForm.meaning) : <span className="text-slate-300 italic">暂无内容...</span>}
                                            </p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm">
                                            <div className="flex items-center gap-2 mb-1 border-b border-blue-50 pb-1">
                                                <Zap size={14} className="text-blue-500"/>
                                                <span className="text-xs font-bold text-blue-900">用法</span>
                                            </div>
                                            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                                                {idiomForm.usage ? renderWithBold(idiomForm.usage) : <span className="text-slate-300 italic">暂无内容...</span>}
                                            </p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-1 border-b border-gray-50 pb-1">
                                                <Feather size={14} className="text-gray-500"/>
                                                <span className="text-xs font-bold text-gray-700">例句</span>
                                            </div>
                                            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                                                {idiomForm.example ? renderWithBold(idiomForm.example) : <span className="text-slate-300 italic">暂无内容...</span>}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col h-full space-y-4">
                                <div>
                                    <input 
                                        type="text" 
                                        placeholder="标题 (例如: 二十四节气...)" 
                                        className="w-full border-b border-gray-200 p-2 text-xl font-bold focus:border-amber-500 outline-none placeholder:text-slate-300" 
                                        value={knowledgeForm.title || ''} 
                                        onChange={e => setKnowledgeForm({...knowledgeForm, title: e.target.value})} 
                                        autoFocus 
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <label className="text-sm font-bold text-slate-500 flex items-center"><Hash size={14} className="mr-1"/>分类标签:</label>
                                        {knowledgeForm.tags?.map(t => (
                                            <span key={t} className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs font-bold flex items-center border border-blue-100">
                                                {t} <button onClick={() => handleRemoveKnowledgeTag(t)} className="ml-1 hover:text-red-500"><X size={12}/></button>
                                            </span>
                                        ))}
                                        <input 
                                            type="text" 
                                            className="text-sm border-b border-gray-300 w-24 px-1 focus:border-blue-500 outline-none bg-transparent"
                                            placeholder="+ 输入"
                                            value={knowledgeTagInput}
                                            onChange={e => setKnowledgeTagInput(e.target.value)}
                                            onKeyDown={e => { if(e.key === 'Enter') handleAddKnowledgeTag(knowledgeTagInput); }}
                                            onBlur={() => handleAddKnowledgeTag(knowledgeTagInput)}
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="text-xs text-slate-400">推荐:</span>
                                        {KNOWLEDGE_TAG_PRESETS[knowledgeForm.type as keyof typeof KNOWLEDGE_TAG_PRESETS]?.map(tag => (
                                            <button 
                                                key={tag} 
                                                onClick={() => handleAddKnowledgeTag(tag)}
                                                className={`text-xs px-2 py-0.5 rounded border transition-colors ${knowledgeForm.tags?.includes(tag) ? 'bg-blue-100 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-slate-500 hover:bg-white hover:border-blue-300'}`}
                                            >
                                                {tag}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                                    <RichTextEditor
                                        value={knowledgeForm.content || ''}
                                        onChange={h => setKnowledgeForm({...knowledgeForm, content: h})}
                                        placeholder="输入知识点详情..."
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                        <button onClick={() => setIsEditingKnowledge(false)} className="px-5 py-2.5 text-sm text-slate-600 font-bold hover:bg-gray-200 rounded-xl transition-colors">取消</button>
                        <button onClick={handleSaveKnowledge} className="bg-amber-500 text-white px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg hover:bg-amber-600 transition-all hover:scale-105">保存知识点</button>
                    </div>
                </div>
            </div>
        )}

        {/* Question Viewer Modal */}
        {viewingQuestion && (
            <div className="fixed inset-0 z-[60] bg-black/50 flex justify-end animate-in fade-in duration-200">
                <div className="w-full max-w-lg bg-white h-full shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800">关联题目预览</h3>
                        <button onClick={() => setViewingQuestion(null)}><X size={24} className="text-slate-400 hover:text-slate-600"/></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="flex gap-2 mb-4">
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">{viewingQuestion.category}</span>
                            <span className="bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded text-xs font-bold">{viewingQuestion.subCategory}</span>
                        </div>
                        <h4 className="font-bold text-slate-800 mb-4">{viewingQuestion.stem}</h4>
                        <div className="space-y-2 mb-6">
                            {viewingQuestion.options.map((o, i) => (
                                <div key={i} className={`p-2 rounded border text-sm flex ${i === viewingQuestion.correctAnswer ? 'bg-green-50 border-green-200 text-green-800' : 'border-gray-100'}`}>
                                    <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2 font-bold">{String.fromCharCode(65+i)}</span>
                                    {o}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => navigate(`/edit/${viewingQuestion.id}`)} className="w-full py-2 border border-blue-200 text-blue-600 rounded-lg text-sm font-bold hover:bg-blue-50">前往编辑详情</button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

// Sub-component for Method Card
const MethodCard = ({ method, questions, onEdit, onDelete, onOpenQuestion, readOnly }: any) => {
    return (
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all relative group cursor-pointer" onClick={onEdit ? undefined : undefined /* handled by parent wrapper click in grid now */}>
            <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg text-slate-800 line-clamp-1">{method.title}</h3>
                {!readOnly && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={16}/></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={16}/></button>
                    </div>
                )}
            </div>
            <div className="prose prose-sm max-w-none text-slate-600 mb-4 rich-text-display bg-slate-50 p-3 rounded-lg border border-slate-100 line-clamp-4" dangerouslySetInnerHTML={{ __html: method.content }}></div>
            
            {questions && questions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-bold text-slate-400 mb-2 flex items-center"><LinkIcon size={12} className="mr-1"/> 关联了 {questions.length} 道题目</p>
                    <div className="flex flex-wrap gap-2">
                        {questions.slice(0, 3).map((q: any) => (
                            <button key={q.id} onClick={(e) => { e.stopPropagation(); onOpenQuestion(q); }} className="max-w-[200px] truncate text-xs bg-white border border-gray-200 text-slate-500 px-2 py-1 rounded hover:text-blue-600 hover:border-blue-300 transition-colors">
                                {q.stem}
                            </button>
                        ))}
                        {questions.length > 3 && <span className="text-xs text-slate-400 self-center">+{questions.length - 3}</span>}
                    </div>
                </div>
            )}
            {(!questions || questions.length === 0) && <p className="text-xs text-slate-300 mt-2 italic">暂无关联题目</p>}
        </div>
    );
};

// Simple Filter Icon for UI
const FilterIcon = ({size, className}: {size:number, className?:string}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
    </svg>
);

export default Methodology;
